// Entry point: styles, shell, routes, first-launch welcome.
import '@fontsource-variable/inter';
import './styles/app.css';
import './styles/ride.css';
import './styles/tour.css';
import './styles/setup.css';
import { route, start, refresh } from './ui/router.js';
import { mountShell } from './ui/shell.js';
import * as home from './ui/screens/home.js';
import { watchFriendRequests } from './data/friends.js';
import { startTutorials } from './ui/tour/index.js';
import { startSync } from './data/sync.js';
import { isLoggedIn, onAuthChange } from './data/api.js';
import { startCulture } from './data/culture.js';
import { onCultureChange, loadDict, lang } from './i18n/index.js';
import { startTheme } from './ui/theme.js';
import { consentGate } from './ui/legal.js';
import { registerFlagFont } from './render/flags.js';

// Every screen but the dashboard is its own chunk, loaded the first time it opens (ui/router.ts); the ride itself
// loads with the first race (ui/ride/start.ts).
const play = () => import('./ui/screens/play.js');
const online = () => import('./ui/screens/online.js');
const events = () => import('./ui/screens/events.js');
const training = () => import('./ui/screens/training.js');
const activities = () => import('./ui/screens/activities.js');
const leaderboards = () => import('./ui/screens/leaderboards.js');
const friends = () => import('./ui/screens/friends.js');
const clubs = () => import('./ui/screens/clubs.js');
const workshop = () => import('./ui/screens/workshop.js');
const store = () => import('./ui/screens/store.js');
const signin = () => import('./ui/screens/signin.js');
route('/', home, 'home');
route('/play', play, 'play');
route('/play/:with', play, 'play');
route('/events', events, 'events');
route('/events/:tab', events, 'events');
route('/event/:id', events, 'events');
route('/event/:id/:edit', events, 'events');
route('/challenges', () => import('./ui/screens/challenges.js'), 'challenges');
route('/online', online, 'online');
route('/online/:code', online, 'online');
route('/watch/:watch', online, 'online'); // the param name tells the screen to watch, not to join
route('/training', training, 'training');
route('/training/:tab', training, 'training');
route('/activities', activities, 'activities');
route('/activity/:id', () => import('./ui/screens/activity.js'), 'activities');
route('/activity/:id/replay', () => import('./ui/screens/replay.js'), 'activities');
route('/progress', () => import('./ui/screens/progress.js'), 'progress');
route('/leaderboards', leaderboards, 'leaderboards');
route('/leaderboards/:scope', leaderboards, 'leaderboards');
route('/friends', friends, 'friends');
route('/friends/add/:code', friends, 'friends');
route('/club', clubs, 'club');
route('/club/join/:code', clubs, 'club');
route('/club/:id', clubs, 'club');
route('/workshop', workshop, 'workshop');
route('/workshop/create/:kind', workshop, 'workshop');
route('/workshop/:id', workshop, 'workshop');
route('/store', store, 'store');
route('/store/:kind', store, 'store');
route('/wardrobe', () => import('./ui/screens/wardrobe.js'), 'wardrobe');
route('/settings', () => import('./ui/screens/settings.js'), 'settings');
route('/settings/:tab', () => import('./ui/screens/settings.js'), 'settings');
route('/tutorials', () => import('./ui/screens/tutorials.js'), 'tutorials');
// terms, privacy policy, legal notice: readable before signing up
route('/legal', () => import('./ui/screens/legal.js'), 'settings');
route('/legal/:page', () => import('./ui/screens/legal.js'), 'settings');
// return from Google or Strava
route('/signin/:provider/error/:reason', signin, 'settings');
route('/signin/:provider/:code', signin, 'settings');
// the link of a password reset e-mail (ADR 0018), opened signed out
route('/reset/:token', () => import('./ui/screens/reset.js'), 'settings');

startTheme();
startCulture();
registerFlagFont();
// the French dictionary is its own chunk: in place before the first screen when the player reads French
await loadDict(lang());
mountShell();
start();
// Language, time zone or clock changed: the current screen is redrawn with the new formats.
onCultureChange(() => refresh());
startSync();
watchFriendRequests();
startTutorials();
// signed in or out: the router swaps the welcome screen and the app (a token refresh changes nothing)
let signedIn = isLoggedIn();
// An account that has not accepted the current terms (older account, Google or Strava sign-up) is asked before anything
// else, except on the legal pages themselves, which stay readable: the gate comes back as soon as the player leaves them.
const gate = () => { if (isLoggedIn()) void consentGate(); };
gate();
document.addEventListener('route', gate);
onAuthChange(s => { if (!!s !== signedIn) { signedIn = !!s; refresh(); } });
